// The information a player needs to compose an army: what a unit counters,
// what it shrugs off, what damage it deals, and how early it acts.
//
// Phase 5 asks that the player "understand unit counters" and "win through
// correct army composition". That decision is made before the battle starts,
// on the Army screen, so the same traits are shown there and in the battle
// itself rather than only once the fighting has begun.

import type { Unit } from "../types"
import { getIconStyle, getUnitSpriteStyle } from "../game/sprites"

/**
 * The small stamp for a damage type or tag, cut from icons.png. Renders nothing
 * for keys the sheet has no symbol for, so it can sit next to any label. The
 * text label beside it carries the meaning, so the icon is decorative here.
 */
function TraitIcon({ name }: { name: string }) {
  const style = getIconStyle(name)
  if (!style) return null
  return <span className="trait-icon" style={style} aria-hidden="true" />
}

/**
 * The unit's portrait, cut from the packed units_*.png sheets. Renders nothing
 * if the unit falls on a sheet that has not been added yet, so callers can drop
 * it in unconditionally. `size` scales the square: sm for dense rows, lg for
 * the faction codex.
 */
export function UnitPortrait({
  unit,
  size = "md",
}: {
  unit: Unit
  size?: "sm" | "md" | "lg"
}) {
  const style = getUnitSpriteStyle(unit.id)
  if (!style) return null
  return (
    <span
      className={`unit-portrait unit-portrait-${size}`}
      style={style}
      role="img"
      aria-label={unit.name}
    />
  )
}

/**
 * The raw combat numbers — attack, defence, health, damage, initiative — shown
 * alongside the traits when a player is inspecting a roster rather than fighting
 * with it. In battle the live health pool matters more than the base numbers,
 * so the battle card leaves this out.
 */
export function UnitStatLine({ unit }: { unit: Unit }) {
  return (
    <span className="unit-stat-line">
      <span className="unit-stat">ATK {unit.attack}</span>
      <span className="unit-stat">DEF {unit.defence}</span>
      <span className="unit-stat">HP {unit.health}</span>
      <span className="unit-stat">
        DMG {unit.damageMin}–{unit.damageMax}
      </span>
      <span className="unit-stat">INIT {unit.initiative}</span>
    </span>
  )
}

export function UnitTraits({ unit }: { unit: Unit }) {
  const counters = Object.entries(unit.counters ?? {})
  return (
    <span className="stack-traits">
      {counters.map(([tag, mult]) => (
        <span key={tag} className="trait trait-counter">
          <TraitIcon name={tag} />
          counters {tag} x{mult}
        </span>
      ))}
      {unit.resistances.length > 0 && (
        <span className="trait trait-resist">
          resists{" "}
          {unit.resistances.map((resist) => (
            <span key={resist} className="trait-inline">
              <TraitIcon name={resist} />
              {resist}
            </span>
          ))}
        </span>
      )}
      <span className="trait trait-damage">
        <TraitIcon name={unit.damageType} />
        {unit.damageType}
      </span>
      <span className="trait trait-initiative">init {unit.initiative}</span>
      {unit.specialAction && (
        <span className="trait trait-special" title={unit.specialAction.description}>
          {unit.specialAction.name}
        </span>
      )}
    </span>
  )
}

/**
 * The unit's prose specialty line — the plain-language version of the numbers
 * above it, and the hint that tells a player *why* a unit is worth bringing
 * before they have ever seen it fight.
 */
export function UnitSpecialty({ unit }: { unit: Unit }) {
  return <span className="stack-specialty">{unit.specialty}</span>
}
